import { useEffect, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Building2, Wallet } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const fmt = (n) => (parseFloat(n) || 0).toLocaleString('fr-FR');

const COLORS_ACTIF = ['#3b82f6', '#0ea5e9', '#06b6d4', '#14b8a6', '#10b981', '#22c55e', '#84cc16'];
const COLORS_PASSIF = ['#f59e0b', '#f97316', '#ef4444', '#ec4899', '#a855f7', '#8b5cf6', '#6366f1', '#0ea5e9'];

export default function BilanDetail({ user, onLogout, type }) {
  // type: 'actif' | 'passif'
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/bilan`)
      .then((res) => setItems(res.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((i) => i.type === type);
  const total = filtered.reduce((s, i) => s + (parseFloat(i.montant) || 0), 0);

  // Groupement par catégorie pour le pie chart + tableau
  const grouped = (() => {
    const map = {};
    for (const it of filtered) {
      const k = it.categorie || 'Divers';
      if (!map[k]) map[k] = { categorie: k, total: 0, entries: [] };
      map[k].entries.push(it);
      map[k].total += parseFloat(it.montant) || 0;
    }
    return Object.values(map).sort((a, b) => b.total - a.total);
  })();

  const isActif = type === 'actif';
  const headerColor = isActif ? 'from-blue-600 to-blue-700' : 'from-amber-600 to-amber-700';
  const accentColor = isActif ? 'blue' : 'amber';
  const Icon = isActif ? Building2 : Wallet;
  const colors = isActif ? COLORS_ACTIF : COLORS_PASSIF;
  const title = isActif ? 'Détail de l\'Actif' : 'Détail du Passif';
  const subtitle = isActif
    ? 'Composition de l\'actif (immobilisations, stocks, créances, disponibilités)'
    : 'Composition du passif (capitaux propres, dettes, provisions)';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 flex-wrap">
          <Button onClick={() => navigate(-1)} variant="outline" className="gap-2" data-testid="bilan-detail-back">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Icon className={`h-7 w-7 text-${accentColor}-600`} /> {title}
            </h1>
            <p className="text-gray-600 mt-1 text-sm">{subtitle}</p>
          </div>
          <div className="ml-auto">
            <Badge className={`bg-${accentColor}-600 text-white text-sm px-4 py-2`} data-testid="detail-total-badge">
              Total : {fmt(total)} €
            </Badge>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : grouped.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-gray-400 italic">
              Aucune ligne enregistrée pour {isActif ? 'l\'actif' : 'le passif'} pour le moment.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Diagramme circulaire */}
            <Card className="shadow-lg">
              <CardHeader className={`bg-gradient-to-r ${headerColor} text-white`}>
                <CardTitle>Répartition par catégorie</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={grouped.map((g) => ({ name: g.categorie, value: g.total }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name.length > 18 ? name.substring(0, 16) + '…' : name}: ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={130}
                      dataKey="value"
                    >
                      {grouped.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => `${fmt(value)} €`}
                      contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Tableau détaillé */}
            <Card className="shadow-lg">
              <CardHeader className={`bg-gradient-to-r ${headerColor} text-white`}>
                <CardTitle>Tableau détaillé ({filtered.length} ligne{filtered.length > 1 ? 's' : ''})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className={`bg-${accentColor}-50 border-b border-${accentColor}-200 sticky top-0`}>
                      <tr>
                        <th className={`px-4 py-2 text-left text-xs font-semibold text-${accentColor}-800 uppercase`}>
                          Libellé
                        </th>
                        <th className={`px-4 py-2 text-right text-xs font-semibold text-${accentColor}-800 uppercase`}>
                          Montant
                        </th>
                        <th className={`px-4 py-2 text-right text-xs font-semibold text-${accentColor}-800 uppercase`}>
                          %
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped.map((g, gi) => (
                        <Fragment key={`g-${gi}`}>
                          <tr className={`bg-${accentColor}-100/70`}>
                            <td colSpan="2" className={`px-3 py-2 text-xs font-bold text-${accentColor}-900 uppercase`}>
                              {g.categorie}
                            </td>
                            <td className={`px-3 py-2 text-right text-xs font-bold text-${accentColor}-900`}>
                              {fmt(g.total)} €
                            </td>
                          </tr>
                          {g.entries.map((e, ei) => {
                            const pct = total > 0 ? ((parseFloat(e.montant) || 0) / total) * 100 : 0;
                            return (
                              <tr key={`e-${gi}-${ei}`} className="hover:bg-gray-50 border-b">
                                <td className="px-4 py-2 pl-8 text-gray-700">{e.libelle || 'Divers'}</td>
                                <td className={`px-4 py-2 text-right font-medium text-${accentColor}-700`}>
                                  {fmt(e.montant)} €
                                </td>
                                <td className="px-4 py-2 text-right text-gray-500">{pct.toFixed(1)}%</td>
                              </tr>
                            );
                          })}
                        </Fragment>
                      ))}
                    </tbody>
                    <tfoot className={`bg-${accentColor}-100 font-bold sticky bottom-0`}>
                      <tr>
                        <td className={`px-4 py-3 text-${accentColor}-900 uppercase tracking-wide`}>
                          Total {isActif ? 'Actif' : 'Passif'}
                        </td>
                        <td className={`px-4 py-3 text-right text-${accentColor}-900`}>{fmt(total)} €</td>
                        <td className={`px-4 py-3 text-right text-${accentColor}-900`}>100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
